/**
 * 操作记录（OperationLog）业务 hooks：列表过滤、关键词可见性、标题、修改前后内容、商品展示文案。
 * 与 OperationLog.tsx 展示层分离，单一职责。
 */

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useRequest } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import { find } from 'lodash-es';
import { productList as getItemList } from 'services/api/conversation';
import { convertMessageToTextAndImage } from './helper';
import { useStatusLabel } from './hook';
import { useResultTypeLabel } from '../../../Todo/hooks/useLabel';
import { childStatusI18nMap } from './childStatusI18nMap';

// ---------- 常量 ----------

/** simple 模式下仍展示的操作类型 */
export const SIMPLE_MODE_OPERATION_TYPES_WHITELIST: Todo.OperateType[] = [
  'CHANGE_STATUS',
  'CHANGE_RESULT',
  'CHANGE_OPERATOR',
  'CREATE',
  'CLOSE',
  'REPLY_AND_CLOSE',
  'REOPEN',
];

/** 需要展示「修改前/修改后」详情块的操作类型 */
export const SHOW_DETAIL_OPERATION_TYPES: Todo.OperateType[] = [
  'CHANGE_DESCRIPTION',
  'CHANGE_RELATED_TRADE_ID',
  'CHANGE_RELATED_ITEM_ID',
  'CHANGE_RESULT',
  'CHANGE_REPLY',
  'CHANGE_SKU',
];

type OpMapKey =
  | 'CHANGE_DESCRIPTION'
  | 'CHANGE_RELATED_TRADE_ID'
  | 'CHANGE_RELATED_ITEM_ID'
  | 'CHANGE_REPLY';

const OP_MAP: Record<OpMapKey, keyof Todo.TodoInstance> = {
  CHANGE_DESCRIPTION: 'description',
  CHANGE_RELATED_TRADE_ID: 'relatedTradeId',
  CHANGE_RELATED_ITEM_ID: 'relatedItemId',
  CHANGE_REPLY: 'replyMsgs',
};

type OperationTypeKey = 'beforeOperation' | 'afterOperation';

// ---------- useOperationLogItems ----------

export function useOperationLogItems(
  dataSource: Todo.TodoInstance['operationLog'],
  simple: boolean,
) {
  return useMemo(() => {
    const itemsToShow = dataSource.filter((item) => {
      if (item.operationType === 'CHANGE_TYPE') return false;
      if (simple && !SIMPLE_MODE_OPERATION_TYPES_WHITELIST.includes(item.operationType)) {
        return false;
      }
      return true;
    });

    const getShowDetail = (item: Todo.TodoInstance['operationLog'][number]): boolean =>
      !simple && SHOW_DETAIL_OPERATION_TYPES.includes(item.operationType);

    return { itemsToShow, getShowDetail };
  }, [dataSource, simple]);
}

// ---------- useKeywordFilterVisibility ----------

export function useKeywordFilterVisibility(keyword: string) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(true);

  useLayoutEffect(() => {
    if (!keyword) {
      setVisible(true);
      return;
    }
    if (ref.current) {
      const mark = ref.current.querySelector('mark');
      setVisible(!!mark);
    }
  }, [keyword]);

  return { ref, visible };
}

// ---------- useOperationTitle ----------

export function useOperationTitle(data: Todo.TodoInstance['operationLog'][number]) {
  const { statusLabel } = useStatusLabel();
  const { t } = useTranslate();
  const { operatorName, operationType, afterOperation } = data;

  return useMemo(() => {
    let opName = operatorName || t('cancelType.system');
    let desc = t(`operateType.${operationType}`);
    if (operationType === 'CHANGE_OPERATOR') {
      return t('operateType.CHANGE_OPERATOR', {
        x: afterOperation?.operatorName,
      });
    }
    if (operationType === 'CHANGE_STATUS') {
      opName = '';
      let statusStr = statusLabel[afterOperation.todoStatus];
      if (afterOperation.todoSubStatus) {
        statusStr += `-${
          childStatusI18nMap[afterOperation.todoSubStatus]
            ? t(childStatusI18nMap[afterOperation.todoSubStatus])
            : afterOperation.todoSubStatus
        }`;
      }
      desc = t('operateType.CHANGE_STATUS', {
        x: statusStr,
      });
    }
    if (operationType === 'CREATE' && operatorName) {
      desc = t('todo_created_self');
    }
    return opName ? `${opName} ${desc}` : desc;
  }, [operatorName, operationType, afterOperation, statusLabel, t]);
}

// ---------- useBeforeAfterContent 返回类型 ----------

export type BeforeAfterContent =
  | { kind: 'textAndImages'; text: string; images: string[] }
  | { kind: 'tradeId'; text: string }
  | {
      kind: 'itemOrSkus';
      itemId?: string;
      skus?: Todo.RelatedSku[];
    }
  | { kind: 'result'; text: string; images: string[] }
  | { kind: 'none' };

// ---------- useBeforeAfterContent ----------

export function useBeforeAfterContent(
  data: Todo.TodoInstance['operationLog'][number],
  when: 'before' | 'after',
  _type: Todo.Type,
): BeforeAfterContent {
  const { t } = useTranslate();
  const { resultTypeLabel } = useResultTypeLabel();
  const { operationType } = data;
  const op = data[`${when}Operation` as OperationTypeKey];

  return useMemo(() => {
    if (operationType === 'CHANGE_DESCRIPTION' || operationType === 'CHANGE_REPLY') {
      const content = op?.[OP_MAP[operationType as OpMapKey] as keyof Todo.TodoInstance];
      const { text, images } = convertMessageToTextAndImage(content as string[]);
      return { kind: 'textAndImages', text, images };
    }
    if (operationType === 'CHANGE_RELATED_TRADE_ID') {
      const content = op?.relatedTradeId ?? '';
      return { kind: 'tradeId', text: content };
    }
    if (operationType === 'CHANGE_RELATED_ITEM_ID' || operationType === 'CHANGE_SKU') {
      const content = op?.relatedItemId;
      if (content) {
        return { kind: 'itemOrSkus', itemId: content };
      }
      const items = op?.skus;
      if (items?.length) {
        return { kind: 'itemOrSkus', skus: items };
      }
      return { kind: 'none' };
    }
    if (operationType === 'CHANGE_RESULT') {
      const f1 = op?.operateResult;
      const f2 = op?.customOperateResult;
      const f3 = op?.testimonies ?? [];
      const text = f1 === 'OTHER' ? f2 || t('resultFallback.OTHER') : resultTypeLabel[f1] ?? '';
      return { kind: 'result', text, images: f3 };
    }
    return { kind: 'none' };
  }, [operationType, op, resultTypeLabel, t]);
}

// ---------- useItemDisplayInfo ----------

export function useItemDisplayInfo(
  itemId: string,
  storeId: string,
  skuId?: string,
  quantity?: number,
) {
  const { data, loading } = useRequest(
    () =>
      getItemList({
        page_no: 1,
        page_size: 10,
        external_item_ids: itemId,
        internal_store_id: storeId,
      }),
    {
      refreshDeps: [itemId],
      ready: !!itemId && !!storeId,
    },
  );

  const displayText = useMemo(() => {
    if (!data?.itemList?.[0]) return null;
    const title = data.itemList[0].itemName;
    const targetSku = find(data.itemList[0].skuInfo, (e: { skuId: string }) => e.skuId === skuId);
    return `[${title}]${targetSku?.skuId ? ` [${targetSku?.sku}] [${targetSku?.sellerSku}]` : ''}[${
      quantity != null ? `x${quantity}` : quantity
    }]`;
  }, [data, skuId, quantity]);

  return { displayText, loading };
}
